import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getSearch,
  setSearchOpenedAction,
  setClearSearchAction,
  setSearchResultsAction,
  setSearchLoadingAction,
  setSearchedAction,
  clearSearchResultsAction,
} from 'features/search/redux/slice';

import { GlobalSearchResponseCollection } from './types';
import { request } from 'app/utils';
import { useSchool } from 'features/school';
import usestorage from 'app/utils/storage';
import { useSearchParams } from 'react-router-dom';

interface FetchSearchProps {
  searchText: string;
  page?: number;
  limit?: number;
  withCache?: boolean;
  productTypes?: number[];
  enrolled?: null | boolean;
}

const useSearch = () => {
  const dispatch = useDispatch();

  const { apiGatewayUrl } = useSchool();

  const search = useSelector(getSearch);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  const setSearchOpened = (opened: boolean) => {
    dispatch(setSearchOpenedAction(opened));
  };

  const setSearchLoading = (loading: boolean) => {
    dispatch(setSearchLoadingAction(loading));
  };

  const setSearched = (searched: boolean) => {
    dispatch(setSearchedAction(searched));
  };

  const setSearchResults = (response: GlobalSearchResponseCollection) => {
    dispatch(setSearchResultsAction(response));
  };

  const clearSearchResults = () => {
    dispatch(clearSearchResultsAction());
  };

  const storage = usestorage('localStorage');
  const currentDashboard = storage.get('currentDashboard');

  const [searchParams] = useSearchParams();
  const currentDashboardArea = searchParams.get('dashboardAreaUuid');

  const fetchSearch = async ({ searchText, page = 1, limit = 25, withCache = true, productTypes = [], enrolled = null }: FetchSearchProps) => {
    setSearchLoading(true);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    request<GlobalSearchResponseCollection>({
      url: `/api/v1/School/SearchProducts`,
      method: 'POST',
      baseURL: apiGatewayUrl,
      data: {
        id: withCache && search.searchId ? search.searchId : undefined,
        text: searchText,
        page,
        limit,
        productTypes,
        enrolled,
      },
      params: {
        ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
        ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
      },
    })
      .then(({ data }) => {
        setSearchResults({ ...data, searchText });
      })
      .finally(() => {
        setSearchLoading(false);
      });
  };

  const clearSearch = (isNull: boolean = false) => {
    dispatch(setClearSearchAction(isNull));
  };

  const handleClickSearchOutside = useCallback(
    (event: any) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setSearchOpened(false);
      }
    },
    [searchWrapperRef]
  );

  const handleEscSearch = useCallback((event) => {
    if (event.key === 'Escape') {
      setSearchOpened(false);
    }
  }, []);

  useEffect(() => {
    if (search.searchOpened) {
      searchInputRef.current && searchInputRef.current.focus();

      document.addEventListener('click', handleClickSearchOutside, true);
      document.addEventListener('keydown', handleEscSearch, false);
    } else {
      searchInputRef.current && searchInputRef.current.blur();

      document.removeEventListener('click', handleClickSearchOutside, true);
      document.removeEventListener('keydown', handleEscSearch, false);
    }
  }, [search.searchOpened]);

  return {
    ...search,

    setSearchOpened,

    searchWrapperRef,
    searchInputRef,

    clearSearch,

    fetchSearch,

    setSearchResults,
    clearSearchResults,

    setSearchLoading,

    setSearched,
  };
};

export default useSearch;
